import { useState, useEffect, startTransition, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { selectUser } from '../../../slices/user/userSlice';
import { selectAllDocuments } from '../../../slices/documents/documentsSlice';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { toast } from 'react-toastify';
import { fetchDisclaimerInfo } from '../../../slices/disclaimer/disclaimerSlice';
import {
  COMPANY_TEMPLATES_CATEGORY,
  fetchLibraryItemDetails,
  fetchLibraryItemHTML,
  resetLibraryItemDetails,
  saveLibraryItemContent,
  selectLibraryItemContentSaveStatus,
  selectLibraryItemDetails,
  selectLibraryItemDetailsUpdateStatus,
  selectLibraryItemHTML,
  selectLibraryItemHTMLStatus,
  setLibraryItemHTML,
  updateLibraryItemDetails,
} from '../../../slices/library-item-details/libraryItemDetailsSlice';
import { TAGS_LABELS } from '../../../common/constants/tags-labels';
import { Helpers } from '../../../common/helpers/helpers';
import { useErrorMessage } from '../../../common/hooks/useErrorMessage';

export const useLibraryItemDetails = () => {
  const { id } = useParams();
  const { name: templateCategory } = useParams();
  const [params] = useSearchParams();
  const currentUser = useAppSelector(selectUser);
  const generateDocumentParam = params.get('generateDocument');
  const editModeParam = params.get('editMode');
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  const [editMode, setEditMode] = useState<boolean>(Boolean(editModeParam));
  const [templateName, setTemplateName] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [documentTypeLabel, setDocumentTypeLabel] = useState<string>('');
  const [documentWorkflowLabel, setDocumentWorkflowLabel] = useState<string>('');
  const [propertiesEditMode, setPropertiesEditMode] = useState<boolean>(false);
  const [contractCreationEnabled, setContractCreationEnabled] = useState<boolean>(
    Boolean(generateDocumentParam)
  );
  const [customLabel, setCustomLabel] = useState<string>('');
  const documents = useAppSelector(selectAllDocuments).filter((el) => el.user === currentUser.id);
  const dispatch = useAppDispatch();

  const templateItem = useAppSelector(selectLibraryItemDetails);
  const dlHTML = useAppSelector(selectLibraryItemHTML);
  const htmlStatus = useAppSelector(selectLibraryItemHTMLStatus);
  const updateStatus = useAppSelector(selectLibraryItemDetailsUpdateStatus);
  const saveStatus = useAppSelector(selectLibraryItemContentSaveStatus);

  // 'idle' counts as loading too, so the overlay is shown until the first request settles.
  const loadingHTML = htmlStatus !== 'succeeded' && htmlStatus !== 'failed';
  const loading = updateStatus === 'updating' || saveStatus === 'saving';

  const setDlHTML = (html: string) => {
    dispatch(setLibraryItemHTML(html));
  };

  const categoryArray = ['Business', 'Customers', 'Employment', 'Suppliers'];

  const categorySelectValues = categoryArray.map((category) => ({
    value: category,
    label: category,
  }));

  const documentWorkflowLabelsEmployment = TAGS_LABELS.documentWorkflow.employment.map((el) =>
    t(el)
  );
  const documentWorkflowLabelsEmploymentSorted = documentWorkflowLabelsEmployment.sort((a, b) =>
    a.localeCompare(b)
  );
  const documentWorkflowLabelsSupplier = TAGS_LABELS.documentWorkflow.supplier.map((el) => t(el));
  const documentWorkflowLabelsSupplierSorted = documentWorkflowLabelsSupplier.sort((a, b) =>
    a.localeCompare(b)
  );
  const documentWorkflowLabelsCustomer = TAGS_LABELS.documentWorkflow.customer.map((el) => t(el));
  const documentWorkflowLabelsCustomerSorted = documentWorkflowLabelsCustomer.sort((a, b) =>
    a.localeCompare(b)
  );
  const documentWorkflowLabelsBusiness = TAGS_LABELS.documentWorkflow.business.map((el) => t(el));
  const documentWorkflowLabelsBusinessSorted = documentWorkflowLabelsBusiness.sort((a, b) =>
    a.localeCompare(b)
  );

  const workflowsArray = () => {
    // Use the current category state rather than documentDetails.category
    const currentCategory = category
      ? category.toLowerCase()
      : templateItem?.category
        ? templateItem.category
        : '';

    switch (currentCategory) {
      case 'employment':
        return documentWorkflowLabelsEmploymentSorted.map((el) => ({ label: el, value: el }));
      case 'suppliers':
        return documentWorkflowLabelsSupplierSorted.map((el) => ({ label: el, value: el }));
      case 'customers':
        return documentWorkflowLabelsCustomerSorted.map((el) => ({ label: el, value: el }));
      case 'business':
        return documentWorkflowLabelsBusinessSorted.map((el) => ({ label: el, value: el }));
      default:
        return [];
    }
  };

  const documentTypeLabels = TAGS_LABELS.documentType.map((el) => t(el));
  const getTypeLabels = () => {
    return documentTypeLabels.map((el) => ({ label: el, value: el }));
  };

  const templateTitle = templateItem
    ? templateItem.name
      ? templateItem.name.replace('.pdf', '').replace('.doc', '').replace('.docx', '')
      : 'document_upload' in templateItem &&
          templateItem.document_upload &&
          'updated' in templateItem &&
          documents?.length > 0
        ? documents?.find((el) => el.id === templateItem.document_upload)?.name +
          '_' +
          templateItem.updated
        : t('templatePage.headerDefault')
    : t('templatePage.headerDefault');

  const updateLibraryItem = () => {
    if (!templateItem) {
      return;
    }
    dispatch(
      updateLibraryItemDetails({
        id: templateItem.id,
        userId: currentUser.id,
        templateName,
        defaultTemplateName: t('labels.templateDefault'),
        category,
        documentTypeLabel,
        documentWorkflowLabel,
        customLabel,
      })
    )
      .unwrap()
      .then(() => {
        toast.success(t('messages.templateUpdated'));
      })
      .catch((error) => {
        toast.error(getErrorMessage(error));
      })
      .finally(() => {
        setPropertiesEditMode(false);
      });
  };

  const handleSave = () => {
    if (!templateItem || !templateCategory) {
      return;
    }
    dispatch(
      saveLibraryItemContent({
        id: templateItem.id,
        templateCategory,
        userId: currentUser.id,
        name: templateItem.name ?? '',
        html: dlHTML,
      })
    )
      .unwrap()
      .then(() => {
        toast.success(t('messages.templateUpdated'));
        setEditMode(false);
      })
      .catch((error) => {
        toast.error(getErrorMessage(error));
      });
  };

  const setBlockInfoInitial = useCallback(() => {
    setTemplateName(templateItem?.name ?? '');
    setCategory(
      templateItem?.category ? Helpers.capitalizeFirstLetter(templateItem?.category) : ''
    );
    setCustomLabel(
      templateItem?.dt_label && templateItem?.dt_label.length > 0 ? templateItem?.dt_label[0] : ''
    );
    setDocumentWorkflowLabel(
      templateItem?.dt_workflows && templateItem?.dt_workflows.length > 0
        ? templateItem?.dt_workflows[0]
        : ''
    );
    setDocumentTypeLabel(
      templateItem?.dt_types && templateItem?.dt_types.length > 0 ? templateItem?.dt_types[0] : ''
    );
  }, [
    templateItem?.category,
    templateItem?.dt_label,
    templateItem?.dt_types,
    templateItem?.dt_workflows,
    templateItem?.name,
  ]);

  useEffect(() => {
    if (templateItem && 'document_upload' in templateItem) {
      startTransition(() => {
        setBlockInfoInitial();
      });
    }
  }, [setBlockInfoInitial, templateItem]);

  useEffect(() => {
    dispatch(fetchDisclaimerInfo());
  }, [dispatch]);

  useEffect(() => {
    if (!id || !templateCategory) {
      return;
    }
    const itemId = Number(id);
    dispatch(fetchLibraryItemDetails({ id: itemId, templateCategory }))
      .unwrap()
      .then(() => {
        if (templateCategory !== COMPANY_TEMPLATES_CATEGORY) {
          return dispatch(fetchLibraryItemHTML(itemId)).unwrap();
        }
      })
      .catch((error) => {
        toast.error(getErrorMessage(error));
      });
  }, [id, templateCategory, dispatch, getErrorMessage]);

  // Don't let the next library item page start with this item's data.
  useEffect(() => {
    return () => {
      dispatch(resetLibraryItemDetails());
    };
  }, [dispatch]);

  return {
    loading,
    dlHTML,
    setDlHTML,
    templateTitle,
    editMode,
    setEditMode,
    loadingHTML,
    contractCreationEnabled,
    setContractCreationEnabled,
    templateCategory,
    templateItem,
    handleSave,
    setPropertiesEditMode,
    documentTypeLabel,
    setDocumentTypeLabel,
    documentWorkflowLabel,
    setDocumentWorkflowLabel,
    propertiesEditMode,
    category,
    setCategory,
    categorySelectValues,
    getTypeLabels,
    templateName,
    setTemplateName,
    setCustomLabel,
    customLabel,
    workflowsArray,
    updateLibraryItem,
    setBlockInfoInitial,
  };
};
